import React from 'react'
import { Control, FormProvider, useForm, useFormState } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Banner, { BannerColor } from '@divvy-web/skylab.banner'
import Drawer, { DrawerActions, DrawerView } from '@divvy-web/skylab.drawer'
import Scrim from '@divvy-web/skylab.scrim'
import Spinner from '@divvy-web/skylab.spinner'

import Divider from '@sputnik/components/Divider'
import PersonInfoForm from '@sputnik/components/PersonInfoForm'
import ResidentialAddressForm from '@sputnik/components/ResidentialAddressForm'
import { AddPersonForm, addPersonForm } from '@sputnik/schema/creditApplication/addPerson/addPersonSchema'
import { RequiredAddress } from '@sputnik/schema/creditApplication/requiredAddress'

import styles from './styles.module.scss'

export interface AddPersonDrawerOpenable {
  isShowing: boolean
  onClose: () => void
}
export interface AddPersonDrawerProps extends AddPersonDrawerOpenable {
  isAuthorizedSigner?: boolean
  title: React.ReactNode
  banner?: React.ReactNode
  initialValues?: () => Promise<AddPersonForm>
  onSubmit: (data: AddPersonForm) => Promise<void>
}

const AddPersonDrawer = ({
  isShowing,
  onClose,
  title,
  onSubmit,
  isAuthorizedSigner,
  initialValues,
  banner,
}: AddPersonDrawerProps) => {
  const [getClassName] = useNameFormatter('AddPersonDrawer')

  const methods = useForm<AddPersonForm>({
    mode: 'all',
    defaultValues: initialValues
      ? async () => initialValues()
      : {
          isForeignAddress: 'NO',
          isNonUSCitizen: false,
        },
    resolver: yupResolver(addPersonForm(isAuthorizedSigner ?? false)),
  })

  const { handleSubmit, reset, control } = methods
  const { isValid, isDirty, isSubmitting, isLoading } = useFormState({ control })

  const onCloseForm = () => {
    reset()
    onClose()
  }

  return (
    <Scrim isShowing={isShowing}>
      <Drawer
        activeViewKey='default'
        className={`${styles[getClassName('')]} fs-unmask`}
        isShowing={isShowing}
        views={{
          default: (
            <DrawerView
              footerActions={
                <DrawerActions
                  cancelText={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Cancel'
                        id='sputnik.AddPersonDrawer__47FYwb'
                      />
                    </span>
                  }
                  className={styles[getClassName('actionButtons')]}
                  confirmButtonColor='action'
                  confirmText={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Save'
                        id='sputnik.AddPersonDrawer__jvo0vs'
                      />
                    </span>
                  }
                  form={getClassName('form')}
                  inactive={!isValid || !isDirty || isSubmitting}
                  onCancel={onCloseForm}
                  onConfirm={handleSubmit(onSubmit)}
                />
              }
              headerLeftIcon='close'
              headerLeftIconName='close'
              title={title}
              onLeftIconClick={onCloseForm}
            >
              {isLoading ? (
                <Spinner centeredWithAbsolutePositioning />
              ) : (
                <FormProvider {...methods}>
                  <form
                    className={styles[getClassName('form')]}
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    {banner && (
                      <Banner
                        backgroundColor={BannerColor.Info}
                        className={styles[getClassName('banner')]}
                        messageContent={banner}
                      />
                    )}
                    <PersonInfoForm
                      className={getClassName('')}
                      control={control}
                      isAuthorizedSigner={isAuthorizedSigner}
                    />
                    <Divider />
                    <ResidentialAddressForm control={control as unknown as Control<RequiredAddress>} />
                  </form>
                </FormProvider>
              )}
            </DrawerView>
          ),
        }}
      />
    </Scrim>
  )
}

export default AddPersonDrawer
